/**
 * Lucide icons, inlined.
 *
 * Copied from lucide-static v1.34.0 (ISC licence, https://lucide.dev) rather
 * than taken as a dependency. Nine icons do not justify a package, and more to
 * the point the game has to work as a static bundle with no network — an icon
 * font or a CDN sprite would be one more thing that can fail to arrive, on a
 * phone, halfway through a session.
 *
 * Inline SVG, not an icon font: it inherits `currentColor` and scales with the
 * button, so a control's icon cannot end up the wrong colour against a dark
 * background or the wrong size on a dense screen. Every path here is stroked,
 * never filled, which is why the wrapper sets `fill="none"` — a filled path
 * with these outlines renders as a blob.
 *
 * The unicode glyphs these replaced (⚙ ▣ ✎) rendered differently on every
 * platform, and one of them arrived as a colour emoji on iOS while its
 * neighbours stayed grey.
 */

/** Path data only; the wrapper below supplies the shared SVG attributes. */
const PATHS: Record<string, string> = {
  map:
    '<path d="M14.106 5.553a2 2 0 0 0 1.788 0l3.659-1.83A1 1 0 0 1 21 4.619v12.764a1 1 0 0 1-.553.894l-4.553 2.277a2 2 0 0 1-1.788 0l-4.212-2.106a2 2 0 0 0-1.788 0l-3.659 1.83A1 1 0 0 1 3 19.381V6.618a1 1 0 0 1 .553-.894l4.553-2.277a2 2 0 0 1 1.788 0z"/><path d="M15 5.764v15"/><path d="M9 3.236v15"/>',
  settings:
    '<path d="M14 17H5"/><path d="M19 7h-9"/><circle cx="17" cy="17" r="3"/><circle cx="7" cy="7" r="3"/>',
  edit:
    '<path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"/>',
  arrowRight: '<path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>',
  retry: '<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>',
  download: '<path d="M12 15V3"/><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="m7 10 5 5 5-5"/>',
  shuffle:
    '<path d="m18 14 4 4-4 4"/><path d="m18 2 4 4-4 4"/><path d="M2 18h1.973a4 4 0 0 0 3.3-1.7l5.454-8.6a4 4 0 0 1 3.3-1.7H22"/><path d="M2 6h1.972a4 4 0 0 1 3.6 2.2"/><path d="M22 18h-6.041a4 4 0 0 1-3.3-1.8l-.359-.45"/>',
  list:
    '<path d="M3 5h.01"/><path d="M3 12h.01"/><path d="M3 19h.01"/><path d="M8 5h13"/><path d="M8 12h13"/><path d="M8 19h13"/>',
  play: '<path d="M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z"/>',
  // lucide "eye" — the driver's-eye view
  eye: '<path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/><circle cx="12" cy="12" r="3"/>',
  // lucide "circle-dot" — the recording control, armed or not
  sound: '<path d="M11 5 6 9H2v6h4l5 4z"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/><path d="M18.5 5.5a9 9 0 0 1 0 13"/>',
  soundOff: '<path d="M11 5 6 9H2v6h4l5 4z"/><path d="m22 9-6 6"/><path d="m16 9 6 6"/>',
  record: '<circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/>',
  // lucide "video" — the chase view
  video: '<path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5"/><rect x="2" y="6" width="14" height="12" rx="2"/>',
};

export type IconName = keyof typeof PATHS;

/**
 * One icon, as an SVG string ready to drop into innerHTML.
 *
 * `aria-hidden` because every control that uses one already carries its own
 * `aria-label` or visible text: a screen reader announcing "map" twice is
 * worse than not announcing the decoration at all.
 */
export function icon(name: IconName, size = 22): string {
  return (
    `<svg class="icon" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" ` +
    `stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" ` +
    `aria-hidden="true" focusable="false">${PATHS[name] ?? ""}</svg>`
  );
}
